import {
  AppDataApiFactory,
  MultiTenancySides,
  TenantApiFactory,
  UserOverrideInput,
  UserOverrideOutput,
} from "./../data/service-proxies/api";
import { AuthProvider } from "react-admin";
import { UserManager, User, Profile } from "oidc-client";
import { History } from "history";
import messages from "../i18n/messages";
import memoizeOne from "memoize-one";
import { ProLogsPermissions } from "./permissions";
import { parseJwt } from "../util/parseJwt";
import { localStorageService } from "../util/localStorageService";

const ADMIN_USER_OVERRIDE = "admin_user_override";

declare global {
  interface Window {
    _env: any;
  }
}

export interface ProLogsUserProfile extends Profile {
  multitenancy_side?: MultiTenancySides;
  tenant_master?: string;
  role_dashboard?: string;
}

export interface IAuthManagerSettings {
  authority: string;
  clientId: string;
  redirectUri: string;
  silentRedirectUri: string;
  postLogoutRedirectUri: string;
  requestedScopes?: string[] | undefined;
}

interface GetAccessTokenOptions {
  ignoreUserOverride?: boolean;
}

interface LogoutFromIdentityProviderProps {
  redirectBack?: boolean;
}

class UserOverride implements UserOverrideOutput {
  name?: string | null;
  accessToken?: string | null;
  id?: string;

  constructor(deserialized?: UserOverrideOutput, serialized?: string) {
    if (deserialized) {
      this.name = deserialized.name;
      this.accessToken = deserialized.accessToken;
      this.id = deserialized.id;
    } else if (serialized) {
      const self = JSON.parse(serialized) as UserOverrideOutput;
      this.name = self.name;
      this.accessToken = self.accessToken;
      this.id = self.id;
    } else {
      throw new Error(
        "The user override output must be supplied, either deserialized or serialized"
      );
    }
  }

  getUser(): User | null {
    if (!this.accessToken) {
      return null;
    }

    const parsedAccessToken = parseJwt(this.accessToken);
    return new User({
      id_token: "",
      access_token: this.accessToken,
      token_type: "jwt",
      scope: parsedAccessToken.scope,
      refresh_token: "",
      session_state: "",
      expires_at: parsedAccessToken.exp,
      state: "",
      profile: parsedAccessToken,
    });
  }
}

export class AuthManager implements AuthProvider {
  private userManager?: UserManager;
  private userOverride?: UserOverride;
  private readonly history: History<unknown>;
  private readonly memoizedGetPermissions: () => Promise<ProLogsPermissions>;
  requestedScopes: string[];

  constructor(history: History<unknown>) {
    const override = sessionStorage.getItem(ADMIN_USER_OVERRIDE);
    if (override) {
      this.userOverride = new UserOverride(undefined, override);
    }

    this.memoizedGetPermissions = memoizeOne(
      async () => await this.getPermissionsFromApi()
    );
    this.history = history;
    this.requestedScopes = [
      "openid",
      "profile",
      "email",
      "offline_access",
      "EldApi",
      "role",
    ];
  }

  init = (settings: IAuthManagerSettings) => {
    if (settings.requestedScopes) {
      this.requestedScopes = [
        ...this.requestedScopes,
        ...settings.requestedScopes,
      ];
    }

    this.userManager = new UserManager({
      client_id: settings.clientId,
      authority: settings.authority,
      redirect_uri: settings.redirectUri,
      silent_redirect_uri: settings.silentRedirectUri,
      post_logout_redirect_uri: settings.postLogoutRedirectUri,
      response_type: "code",
      scope: this.requestedScopes.reduce(
        (prevState, current) => prevState + " " + current,
        ""
      ),
      filterProtocolClaims: true,
      loadUserInfo: true,
    });

    // Oidc.Log.logger = console;
    // Oidc.Log.level = Oidc.Log.DEBUG;
  };

  addUserLoadedEvent = (fn: (user: User) => void) => {
    if (!this.userManager) {
      throw new Error(messages.AUTH_CALL_INIT_FIRST);
    }

    const callback = (user: User) => {
      fn(user);
      this.userManager?.events.removeUserLoaded(callback);
    };

    this.userManager.events.addUserLoaded(callback);
  };

  login = async (params: any) => {
    // By default, react-admin calls this on Login component submit.
    // Instead, we call it as soon as the Login component renders.

    if (params && params.returnTo) {
      localStorageService.setRedirect(this.history.location);
    }

    if (!this.userManager) {
      throw new Error(messages.AUTH_CALL_INIT_FIRST);
    }

    return await this.userManager.signinRedirect();
  };

  handleAuthentication = async () => {
    if (!this.userManager) {
      throw new Error(messages.AUTH_CALL_INIT_FIRST);
    }

    try {
      const user = await this.userManager.signinCallback();
      const location = localStorageService.getRedirect();
      if (location) {
        document.location.href = !location.search
          ? location.pathname
          : location.pathname + location.search;
      }

      return user;
    } catch {
      document.location.href = "/";
    }
  };

  logout = async (params: any) => {
    if (!this.userManager) {
      throw new Error(messages.AUTH_CALL_INIT_FIRST);
    }

    this.userOverride = undefined;
    sessionStorage.removeItem(ADMIN_USER_OVERRIDE);
    const user = await this.userManager.getUser();
    if (user) {
      return "/logout";
    }

    return;
  };

  logoutFromIdentityProvider = async (
    params?: LogoutFromIdentityProviderProps
  ) => {
    if (!this.userManager) {
      throw new Error(messages.AUTH_CALL_INIT_FIRST);
    }

    this.userOverride = undefined;
    sessionStorage.removeItem(ADMIN_USER_OVERRIDE);
    const user = await this.userManager.getUser();
    if (user) {
      if (params?.redirectBack) {
        localStorageService.setRedirect(this.history.location);
      }

      await this.userManager.signoutRedirect();
    }
  };

  checkAuth = async (params: any = {}) => {
    // Each time the user navigates, react-admin calls the authProvider.checkAuth() method,
    // so it’s the ideal place to validate the credentials.
    // If the promise is rejected, react-admin redirects by default to the /login page.
    // You can override where to redirect the user in checkAuth(), by rejecting an object with a redirectTo property.
    // Note that react-admin will call the authProvider.logout() method before redirecting.
    // If you specify the redirectTo here, it will override the url which may have been returned by the call to logout().
    if (!this.userManager) {
      throw new Error(messages.AUTH_CALL_INIT_FIRST);
    }

    let user =
      this.userOverride?.getUser() ?? (await this.userManager.getUser());
    const redirect = localStorageService.getRedirect(true);
    if (!user || user.expired) {
      if (
        params !== null &&
        this.history.location.pathname !== "/callback" &&
        this.history.location.pathname !== "/logged-out" &&
        !redirect
      ) {
        localStorageService.setRedirect(this.history.location);
      }

      return Promise.reject();
    }

    return Promise.resolve();
  };

  checkError = (error: any) => {
    // each time the API returns an error, react-admin calls the authProvider.checkError() method.
    // Decide which HTTP status codes should let the user continue (by returning a resolved promise)
    // or log them out (by returning a rejected promise).
    const status = error.status;
    if (status === 401) {
      // log out
      return Promise.reject();
    }

    // continue
    return Promise.resolve();
  };

  getAccessToken = async (options?: GetAccessTokenOptions) => {
    if (!this.userManager) {
      throw new Error(messages.AUTH_CALL_INIT_FIRST);
    }

    const user = options?.ignoreUserOverride
      ? await this.userManager.getUser()
      : this.userOverride?.getUser() ?? (await this.userManager.getUser());
    if (!user) {
      return Promise.reject();
    }

    return user.access_token;
  };

  getPermissions = async (params: any) => {
    const user =
      this.userOverride?.getUser() ?? (await this.userManager?.getUser());
    if (!user) {
      console.warn(
        `Requested permissions by '${this.history.location.pathname}', but the user is not loaded`
      );
      return {};
    }

    const perms = await this.memoizedGetPermissions();
    return perms;
  };

  getIdentity = async () => {
    if (!this.userManager) {
      throw new Error(messages.AUTH_CALL_INIT_FIRST);
    }

    const user =
      this.userOverride?.getUser() ?? (await this.userManager.getUser());
    if (!user) {
      return { id: "" };
    }

    const { sub, ...profile } = user.profile as ProLogsUserProfile;
    const getFullName = () => {
      if (this.isUserOverride()) {
        return `MASTER as ${this.userOverride?.name} Admin`;
      } else {
        return profile.multitenancy_side === MultiTenancySides.Host
          ? `${profile.tenant_master ?? "MASTER"}/${profile.given_name} ${
              profile.family_name
            }`
          : `${profile.given_name} ${profile.family_name}`;
      }
    };

    return {
      ...profile,
      id: sub,
      fullName: getFullName(),
    };
  };

  setUserOverride = async (input?: UserOverrideInput) => {
    if (!input) {
      this.userOverride = undefined;
      sessionStorage.removeItem(ADMIN_USER_OVERRIDE);
      return;
    }

    const result = await TenantApiFactory(
      undefined,
      process.env["REACT_APP_API_URL"]?.replace(/\/+$/, "") ??
        "https://localhost:44327"
    ).apiAppTenantAcquiretenantadminaccesstokenPost(
      {
        ...input,
        requestedScopes: this.requestedScopes,
      },
      {
        headers: {
          Authorization:
            "Bearer " +
            (await this.getAccessToken({ ignoreUserOverride: true })),
        },
      }
    );

    sessionStorage.setItem(ADMIN_USER_OVERRIDE, JSON.stringify(result.data));
    this.userOverride = new UserOverride(result.data);
  };

  isUserOverride = () => {
    return !!this.userOverride;
  };

  private getPermissionsFromApi = async (): Promise<ProLogsPermissions> => {
    try {
      const permissionsFromApi = (
        await AppDataApiFactory(
          undefined,
          process.env["REACT_APP_API_URL"]?.replace(/\/+$/, "") ??
            "https://localhost:44327"
        ).apiAppAppdataUserpermissionsGet({
          headers: { Authorization: "Bearer " + (await this.getAccessToken()) },
        })
      ).data;
      if (!permissionsFromApi) {
        return {};
      }

      const actionPermissions =
        permissionsFromApi.actionPermissionGroups?.reduce(
          (previousResult, currentGroup) => {
            const permissionsFromThisGroup =
              currentGroup.permissions
                ?.filter((x) => x && x.isGranted && x.name)
                .reduce(
                  (previousPermissions, currentPermission) => ({
                    ...previousPermissions,
                    [currentPermission.name!]: currentPermission.isGranted,
                  }),
                  {}
                ) ?? {};
            return { ...previousResult, ...permissionsFromThisGroup };
          },
          { __: false }
        ) ?? {};
      return {
        actionPermissions,
        fieldPermissions: permissionsFromApi.fieldPermissionGroups ?? [],
      };
    } catch {
      return {};
    }
  };
}
